/**
 * <KpiCard> — canonical dashboard KPI tile.
 *
 * Locks the widely-drifted KPI typography per docs/design-system/02-typography.md
 * (kpi-value + kpi-label) and the card padding from 03-spacing-layout.md.
 * Replaces the ~14 ad-hoc MetricCard / StatsCards / etc. variants found
 * in Phase-1 §1.2 once Phase 13 sweeps them.
 *
 * Composition:
 *
 *   <KpiCard
 *     label='Open work orders'
 *     value={42}
 *     delta={{ value: -8, direction: 'down', tone: 'positive' }}
 *     freshness={lastUpdatedMs}
 *     icon={<Icon as={Wrench} size='sm' />}
 *   />
 *
 * Size hints (compact / default) drive padding + font sizing. Compact
 * variant is intended for dense dashboards (8 KPIs in a row) — default
 * is for executive overviews with 3-4 KPIs.
 *
 * Spec: docs/design-system/05-components-overview.md, 11-domain-patterns.md.
 */

import { type ReactNode } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Card } from '../../primitives/Card';
import { Text } from '../../primitives/Text';
import { Icon } from '../../primitives/Icon';
import { Skeleton } from '../Skeleton';
import { DataFreshness } from '../../patterns/DataFreshness';

export type KpiDeltaDirection = 'up' | 'down' | 'flat';
export type KpiDeltaTone = 'positive' | 'negative' | 'neutral';
export type KpiCardSize = 'compact' | 'default';

export interface KpiDelta {
  /** Numeric delta (use with `format` for "+12%" rendering). */
  value: number;
  /** Visual direction — drives the icon. */
  direction: KpiDeltaDirection;
  /**
   * Semantic tone — drives color. Decoupled from direction because
   * 'down' is good for some metrics (downtime) and bad for others
   * (open work orders).
   */
  tone: KpiDeltaTone;
  /** Custom formatter. Default `${signedValue}%`. */
  format?: (value: number) => string;
  /** Comparison label. e.g. 'vs last week'. */
  comparedTo?: string;
}

export interface KpiCardProps {
  /** Label rendered above the value (text-xs uppercase muted). */
  label: ReactNode;
  /** Primary metric value. Numbers use tabular-nums. */
  value: ReactNode;
  /** Trend indicator. */
  delta?: KpiDelta;
  /** Sub-line explaining the metric. */
  description?: ReactNode;
  /** Optional Lucide icon next to the label. */
  icon?: ReactNode;
  /** Last-updated timestamp surfaces a <DataFreshness> indicator. */
  freshness?: number | Date | string;
  /** Sparkline / chart slot. */
  chart?: ReactNode;
  /** Card click handler. */
  onClick?: () => void;
  /** Loading state — renders a Skeleton in place of the value. */
  isLoading?: boolean;
  size?: KpiCardSize;
  className?: string;
}

const DELTA_TONE_CLASS: Record<KpiDeltaTone, string> = {
  positive: 'text-success-muted',
  negative: 'text-danger-muted',
  neutral: 'text-muted',
};

const DELTA_ICON: Record<KpiDeltaDirection, typeof ArrowUpRight> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: ArrowRight,
};

function defaultDeltaFormat(value: number): string {
  const signed = value > 0 ? `+${value}` : value === 0 ? '0' : `${value}`;
  return `${signed}%`;
}

export function KpiCard({
  label,
  value,
  delta,
  description,
  icon,
  freshness,
  chart,
  onClick,
  isLoading = false,
  size = 'default',
  className,
}: KpiCardProps) {
  const isCompact = size === 'compact';
  return (
    <Card
      padding={isCompact ? 'sm' : 'md'}
      interactive={!!onClick}
      onClick={onClick}
      className={cn('flex flex-col gap-2 min-w-0', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {icon && <span className="text-muted shrink-0">{icon}</span>}
          <Text variant="kpi-label" tone="muted" as="span" className="truncate">
            {label}
          </Text>
        </div>
        {freshness !== undefined && <DataFreshness updatedAt={freshness} showDot={false} />}
      </div>

      <div className="flex items-baseline gap-2 min-w-0">
        {isLoading ? (
          <Skeleton variant="rect" width="6rem" height={isCompact ? '1.5rem' : '2rem'} />
        ) : (
          <Text
            variant="kpi-value"
            as="span"
            className={cn('truncate', isCompact && 'text-xl')}
          >
            {value}
          </Text>
        )}
        {delta && !isLoading && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-medium tabular-nums',
              DELTA_TONE_CLASS[delta.tone],
            )}
          >
            <Icon as={DELTA_ICON[delta.direction]} size="xs" />
            {(delta.format ?? defaultDeltaFormat)(delta.value)}
          </span>
        )}
      </div>

      {(description || delta?.comparedTo) && (
        <Text variant="caption" tone="muted">
          {description}
          {description && delta?.comparedTo && ' '}
          {delta?.comparedTo && <span className="text-subtle">{delta.comparedTo}</span>}
        </Text>
      )}

      {chart && <div className="mt-1 -mx-1">{chart}</div>}
    </Card>
  );
}

KpiCard.displayName = 'KpiCard';

export default KpiCard;
